import React, { useEffect, useState, lazy, Suspense, useCallback } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { Actions, Container, IcsLink } from "./styles";
import KLCustomerBookingProgress from "../../components/KLCustomerBookingProgress";
import { InfoText, Subtitle, TextIconContainer, Title } from "../styles";
import { StorageKey, StorageKeys } from "../../constants/localStorage";
import { clearLocalStorage } from "../../utils";
import { downloadIcs } from "../../utils/calendarIcs";
import { ServiceWithId } from "../../constants/types";
import KLEventAvailableIcon from "../../components/icons/KLEventAvailableIcon";
import colors from "../../constants/colors";
import {
  successContainerVariants,
  successIconVariants,
  successTitleVariants,
  successSubtitleVariants,
  successInfoVariants,
} from "../../animations/successCelebrations";

// Lazy-load confetti for better bundle size
const Confetti = lazy(() => import("react-confetti"));

interface SuccessNavState {
  slot?: { start: string; end: string };
  service?: ServiceWithId;
}

const CustomerSuccessScreen: React.FC = () => {
  const location = useLocation();
  const navState = (location.state as SuccessNavState | null) || {};

  const { width, height } = useWindowSize();
  const reducedMotion = useReducedMotion();
  const [showConfetti, setShowConfetti] = useState(true);

  const businessName = useSelector(
    (state: RootState) => state.customers.business?.businessName,
  );

  useEffect(() => {
    const keysToClear: StorageKey[] = [
      StorageKeys.BusinessId,
      StorageKeys.SelectedService,
      StorageKeys.CurrentStep,
      StorageKeys.MaxStepReached,
      StorageKeys.IsRedirecting,
      StorageKeys.CustomerName,
      StorageKeys.CustomerPhone,
    ];
    clearLocalStorage(keysToClear);

    const timer = setTimeout(() => setShowConfetti(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  const canDownloadIcs = Boolean(navState.slot && navState.service);

  const handleDownloadIcs = useCallback(() => {
    if (!navState.slot || !navState.service) return;
    const title = businessName
      ? `${businessName} — ${navState.service.description}`
      : navState.service.description;
    downloadIcs({
      title,
      description: `${navState.service.description}${
        navState.service.price ? ` (${navState.service.price} ש״ח)` : ""
      }`,
      location: businessName,
      start: navState.slot.start,
      end: navState.slot.end,
    });
  }, [navState.slot, navState.service, businessName]);

  return (
    <>
      {showConfetti && !reducedMotion && (
        <Suspense fallback={null}>
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
            tweenDuration={3000}
            colors={[colors.PURPLE, colors.PURPLE_LIGHT, colors.PURPLE_LIGHTER, colors.YELLOW_LIGHT]}
          />
        </Suspense>
      )}
      <Container>
        <KLCustomerBookingProgress currentStep={3} />
        <motion.div
          variants={reducedMotion ? undefined : successContainerVariants}
          initial="hidden"
          animate="visible"
        >
          <TextIconContainer
            as={motion.div}
            variants={reducedMotion ? undefined : successIconVariants}
          >
            <KLEventAvailableIcon size="large" withGlow={true} />
          </TextIconContainer>
          <Title
            as={motion.div}
            variants={reducedMotion ? undefined : successTitleVariants}
          >
            איזה כיף, קבענו לי :)
          </Title>
          <Subtitle
            as={motion.div}
            variants={reducedMotion ? undefined : successSubtitleVariants}
          >
            מחכים ומצפים לכם ביום ובשעה שקבעתם!
          </Subtitle>
          <InfoText
            as={motion.div}
            variants={reducedMotion ? undefined : successInfoVariants}
          >
            שלחנו לכם הודעת סמס עם כל הפרטים, ליתר ביטחון.
          </InfoText>
        </motion.div>
        {canDownloadIcs && (
          <Actions>
            <IcsLink type="button" onClick={handleDownloadIcs}>
              הוסיפו ליומן
            </IcsLink>
          </Actions>
        )}
      </Container>
    </>
  );
};

export default CustomerSuccessScreen;
